import * as R from 'ramda'
import { capitalize } from 'lodash-es'
import { format, isBefore } from 'date-fns'

import { formatDayAndHour } from '../../libs/date'
import {
  INCIDENTS_CODES_ALLOWED_FOR_RESOLUTION,
  RESOLVED_STATUS_CODES,
  SHIPPING_STATUS_CODES,
  SHIPPING_WITH_ERE,
  SHIPPING_WITH_INSURANCE_MERCHAND_CODE,
  SHIPPING_WITH_REFUND_CODE,
  SHIPPING_WITH_RETURN,
  SHIPPING_WITH_SLIP_SCAN,
} from '../../common/constants'
import { SHIPPING_STATES } from './constants'
import { getColorChipForStatusState } from '../../context/businessConstants'
import { TRACKING_OPTIONS } from '../../App/ShippingsTracking/TrackingTypeSwitcher'

const findAdditional = (additionals, additionalCode) =>
  additionals.find(({ additional_code }) => additional_code === additionalCode)

const getShippingState = shippingStateCode => {
  return SHIPPING_STATES[shippingStateCode]
}

const isReturned = shipping =>
  shipping.related_services?.find(
    service =>
      (service.relation_type === 'original_shipping' &&
        service.relation_sub_type === 'RETURN-ON-DELIVERY') ||
      (service.relation_type === 'original_shipping' &&
        service.relation_sub_type === 'RETURN'),
  )?.shipping_code

const isUnscheduled = shipping => {
  return (
    !isReturned(shipping) &&
    // shipping.pickup === null &&
    !shipping.is_fixed_pickup &&
    shipping.shipping_status_code === SHIPPING_STATUS_CODES.manifested &&
    !shipping.previous_shipping_code &&
    !findAdditional(shipping.additionals, 'ERE')
  )
}

const transformFiltersToBody = (filters, allowCancelled) => {
  const body = {}

  if (!R.isNil(filters?.clientCenterCode)) {
    body.client_center_code = filters.clientCenterCode
  } else if (!R.isNil(filters?.centres)) {
    if (Array.isArray(filters.centres)) {
      body.client_center_code = filters.centres
        .map(centre => centre.hideId)
        .join(',')
    } else {
      body.client_center_code = filters.centres
    }
  }

  if (!R.isNil(filters?.rowsPerPage)) {
    body.page_limit = filters.rowsPerPage.toString()
  }

  if (!R.isNil(filters?.page)) {
    body.page_offsets = (Number(filters.page) + 1).toString()
  }

  if (!R.isNil(filters?.serviceType)) {
    const selectedServiceType = filters.serviceType.split('-')[0]
    body.shipping_type_code = selectedServiceType
  }

  if (!R.isNil(filters?.postalCodeFrom)) {
    body[`${filters.postalCodeSource}_postal_code`] = !R.isNil(
      filters?.postalCodeTo,
    )
      ? `${filters.postalCodeFrom}[range]${filters.postalCodeTo}`
      : filters.postalCodeFrom
  }

  if (!R.isNil(filters?.originPostalCode)) {
    body.origin_postal_code = filters.originPostalCode
  }

  if (!R.isNil(filters?.destinPostalCode)) {
    body.destin_postal_code = filters.destinPostalCode
  }

  if (!R.isNil(filters?.country)) {
    body.destin_country_code = filters.country
  }

  if (!R.isNil(filters?.dateFrom)) {
    body.shipping_date = !R.isNil(filters?.dateTo)
      ? `${filters.dateFrom}[range]${filters.dateTo}`
      : filters.dateFrom
  }

  if (!R.isNil(filters?.statusCode)) {
    body.shipping_status_code = filters.statusCode
      .filter(code => code !== SHIPPING_STATUS_CODES.cancelled)
      .join(',')

    if (filters.statusCode.includes(SHIPPING_STATUS_CODES.cancelled)) {
      if (filters.statusCode?.length > 1) {
        body.is_nulled = 'true'
      } else if (
        R.isNil(filters?.isUnscheduledPickupSearch) &&
        !filters.search?.length
      ) {
        body.query = 'previous_shipping_code ne null or is_fixed_pickup ne null'
        body.is_nulled = 'false'
      }
    }
  }

  if (!R.isNil(filters?.hasIncident)) {
    if (filters?.incidentCode?.length) {
      const statusArray = []
      const incidentCodes = []

      filters.incidentCode.forEach(item => {
        if (item.includes('&')) {
          const parts = item.split('&')
          incidentCodes.push(parts[0])
          statusArray.push(parts[1])
        } else {
          incidentCodes.push(item)
        }
      })

      if (statusArray.length) {
        body.shipping_status_code = statusArray.join(',')
      }
      body.incident_type_code = incidentCodes.join(',')
    } else {
      body.incident_type_code = filters.hasIncident ? '[not][empty]' : '[null]'
    }
  }

  if (!R.isNil(filters?.sortColumn)) {
    const sortType = filters?.sortType === 'asc' ? '+' : '-'
    const sortDataId =
      {
        shippingDate: 'shipping_date',
        shippingStatusCode: 'shipping_status_code',
        clientCenterCode: 'client_center_code',
        senderAddress2: 'sender_address_2',
        clientReference: 'client_references',
        destinAddress2: 'recipient_address_2',
        recipientName: 'recipient_name',
      }[filters.sortColumn] || 'shipping_code'
    body.order_by = `${sortType}${sortDataId}`
  }

  if (!R.isNil(filters?.shippingReference)) {
    body.client_reference = filters.shippingReference
  }

  if (!R.isNil(filters?.senderAddress2)) {
    body.sender_address_2 = `[contains]${filters.senderAddress2}`
  }

  if (!R.isNil(filters?.recipientAddress2)) {
    body.recipient_address_2 = `[contains]${filters.recipientAddress2}`
  }

  if (!R.isNil(filters?.isUnscheduledPickupSearch)) {
    body.is_nulled = 'false'
    body.query = 'previous_shipping_code eq null and is_fixed_pickup eq null'
  } else if (!R.isNil(filters?.search)) {
    body.query = `shipping_code contains '${filters.search}' or client_references contains '${filters.search}'`
  } else {
    const query = []

    if (!R.isNil(filters?.shippingCode)) {
      query.push(`shipping_code contains '${filters.shippingCode}'`)
    }

    if (!R.isNil(filters?.clientReference)) {
      query.push(`client_references contains '${filters.clientReference}'`)
    }

    if (query.length) {
      body.query = query.join(' and ')
    }
  }
  body.mapping_table_code = 'WEBCLIENT'
  body.projection = `is_fixed_pickup,sender_name,item_count,sender_postal_code,recipient_postal_code,incident_type_code,best_shipping_type_code,declared_shipping_type_code,shipping_type_code,related_services,previous_shipping_code,is_nulled,pickup,additionals,shipping_weight_declared,item_count,shipping_code,manifest_datetime,client_center_code,client_references,sender_address_2,recipient_address_2,recipient_name`

  return body
}

const transformFiltersToBodyFile = filters => {
  const body = {}

  if (!R.isNil(filters?.clientCenterCode)) {
    body.client_center_code = filters.clientCenterCode
  } else if (!R.isNil(filters?.centres)) {
    if (Array.isArray(filters.centres)) {
      body.client_center_code = filters.centres
        .map(centre => centre.hideId)
        .join(',')
    } else {
      body.client_center_code = filters.centres
    }
  }

  if (!R.isNil(filters?.country)) {
    body.country_code = filters.country
  }

  if (!R.isNil(filters?.originPostalCode)) {
    body.origin_postal_code_start = filters.originPostalCode
  }

  if (!R.isNil(filters?.destinPostalCode)) {
    body.destin_postal_code_start = filters.destinPostalCode
  }

  if (!R.isNil(filters?.dateFrom)) {
    body.date_from = filters.dateFrom || null
  }

  if (!R.isNil(filters?.dateTo)) {
    body.date_to = filters.dateTo || null
  }

  if (!R.isNil(filters?.statusCode) && filters.statusCode.length) {
    body.shipping_status_codes = filters.statusCode
      .filter(code => code !== SHIPPING_STATUS_CODES.cancelled)
      .join(',')

    if (filters.statusCode.includes(SHIPPING_STATUS_CODES.cancelled)) {
      if (filters.statusCode.length > 1) {
        body.is_nulled = 'true'
      }
    } else if (
      R.isNil(filters?.isUnscheduledPickupSearch) &&
      !filters.search?.length
    ) {
      body.query = 'previous_shipping_code ne null or is_fixed_pickup ne null'
      body.is_nulled = 'false'
    }
  }

  if (!R.isNil(filters?.hasIncident)) {
    if (filters.incidentCode?.length) {
      const statusArray = []
      const incidentCodes = []

      filters.incidentCode.forEach(item => {
        if (item.includes('&')) {
          const parts = item.split('&')
          incidentCodes.push(parts[0])
          statusArray.push(parts[1])
        } else {
          incidentCodes.push(item)
        }
      })

      if (statusArray.length) {
        body.shipping_status_codes = statusArray.join(',')
      }
      body.incident_type_code = incidentCodes.join(',')
    } else {
      body.incident_type_code = filters.hasIncident ? '[not][empty]' : '[null]'
    }
  }

  body.shipping_status_mapping_from = 'WEBCLIENT'
  body.view_type = 'WEB_SHIPPING_TRACKING'

  return body
}

const parseShipping = (shipping, statusList, states) => {
  const corruptedStatus = {
    code: null,
    reason: '-',
    name: '-',
    canCancel: false,
    canPrint: false,
  }

  const lastStatusEvent = states?.length > 0 ? states[states?.length - 1] : null

  const getStatus = currentStatus =>
    statusList?.find(status => status.code === currentStatus) ?? corruptedStatus

  const statusSeverityColor = getColorChipForStatusState({
    statusCode: shipping.lastStatusCode,
    incidentCode: lastStatusEvent?.incidentCode
      ? lastStatusEvent?.incidentCode
      : lastStatusEvent?.lastStatusCode === 'WEB1600',
    isCancelled: shipping.is_nulled,
    isUnscheduledPickup: isUnscheduled(shipping),
  })

  const getOriginalShippingCode = shipping =>
    shipping.related_services?.find(
      service =>
        (service.relation_type === 'original_shipping' &&
          service.relation_sub_type === 'RETURN') ||
        (service.relation_type === 'original_shipping' &&
          service.relation_sub_type === 'REVERSE-LOGISTICS'),
    )?.shipping_code

  const getReturnShippingCode = shipping =>
    shipping.related_services?.find(
      service => service.relation_type === 'return_shipping',
    )?.shipping_code

  const getReverseReturnShippingCode = shipping =>
    shipping.related_services?.find(
      service => service.relation_type === 'reverse_logistics_shipping',
    )?.shipping_code

  const getReturnOnDeliveryShippingCode = shipping =>
    shipping.related_services?.find(
      service => service.relation_type === 'return_on_delivery_shipping',
    )?.shipping_code

  const parseString = shipping => {
    if (
      shipping?.incident_type_code === '2_INCT' &&
      (getStatus(shipping.shipping_status_code).name === 'Recogido' ||
        getStatus(shipping.shipping_status_code).name === 'Grabado')
    ) {
      return capitalize(
        shipping.incident_type_desc?.replace('DESTINATARIO', 'CLIENTE'),
      )
    }
    return capitalize(shipping.incident_type_desc)
  }

  return {
    info: {
      additionals: shipping.additionals,
      previousShippingCode: shipping.previous_shipping_code,
      relatedServices: shipping.related_services,
      shippingCode: shipping.shipping_code,
      isCancelled: shipping.is_nulled,
      cancellationDatetime:
        shipping.nulled_datetime === '' ||
        shipping.nulled_datetime === null ||
        shipping.nulled_datetime === undefined
          ? undefined
          : new Date(shipping.nulled_datetime),
      shippingStatusCode: {
        ...getStatus(shipping.shipping_status_code),
        severity: statusSeverityColor,
      },
      lastShippingStatusCode: lastStatusEvent,
      clientCenterCode: shipping.client_center_code,
      committedDeliveryDatetime:
        shipping.committed_delivery_datetime === '' ||
        shipping.committed_delivery_datetime === null ||
        shipping.committed_delivery_datetime === undefined
          ? undefined
          : format(
              new Date(shipping.committed_delivery_datetime),
              'dd/MM/yyyy',
            ),
      departmentCode: shipping.department_code ?? '',
      clientBarCode: shipping.client_bar_code ?? '',
      shippingTypeCode: shipping.shipping_type_code ?? '',
      clientReference: shipping.client_references?.length
        ? shipping.client_references[0]
        : '',
      clientReference2:
        shipping.client_references?.length > 1
          ? shipping.client_references[1]
          : '',
      contactName: shipping.delivery?.contactName ?? '',
      hasPickupRequest: false,
      itemCount: shipping.item_count,
      recipientAddress: shipping.recipient_address,
      destinCountryCode:
        lastStatusEvent?.destinCountryCode ?? shipping.destin_country_code,
      destinPostalCode:
        lastStatusEvent?.destinPostalCode ?? shipping.destin_postal_code,
      destinAddress: lastStatusEvent?.destinAddress ?? shipping.destin_address,
      destinAddress2:
        lastStatusEvent?.destinAddress2 ?? shipping.destin_town_name,
      recipientEmail: shipping.recipient_email_notify_address ?? '',
      recipientIdentificationNumber:
        shipping.recipient_identification_number ?? '',
      recipientPhone: shipping.recipient_phones
        ? shipping.recipient_phones[0]
        : '',
      recipientName: shipping.recipient_name,
      recipientPostalCode: shipping.recipient_postal_code,
      senderPostalCode: shipping.sender_postal_code,
      recipientAddress2: shipping.recipient_address_2,
      referralName: shipping.delivery?.referral_name ?? '',
      refundValue:
        findAdditional(shipping.additionals ?? [], SHIPPING_WITH_REFUND_CODE)
          ?.additional_value ?? '',
      hasReturn: !!findAdditional(
        shipping.additionals ?? [],
        SHIPPING_WITH_REFUND_CODE,
      ),
      isSaturdayDelivery: false,
      senderAddress: shipping.sender_address,
      originCountryCode: shipping.origin_country_code,
      recipentEmail: shipping.recipient_email_notify_address ?? '',
      recipentPhone: shipping.recipient_phones ?? '',
      senderPhone: shipping.sender_phones ? shipping.sender_phones[0] : '',
      senderName: shipping.sender_name,
      originPostalCode: shipping.origin_postal_code,
      senderAddress2: shipping.sender_address_2,
      shippingItems: shipping.items,
      shippingComments: shipping.pickup?.comments ?? '',
      bestShippingTypeCode: shipping?.best_shipping_type_code,
      cttPrimeShippingCode: shipping?.ctt_prime_shipping_code,
      declaredShippingTypeCode: shipping?.declared_shipping_type_code,

      shippingValue: 0,
      shippingWeight: shipping.shipping_weight_declared,
      declaredDeliveryDatetime: shipping.pickup?.date ?? '',
      shippingDate: shipping.shipping_date
        ? format(new Date(shipping.shipping_date), 'dd/MM/yyyy')
        : '',
      manifestDate: shipping.manifest_datetime,
      conversionComments: shipping.delivery?.comments ?? '',
      services: shipping.additionals ?? [],
      shippingStatusDatetime: shipping.shipping_status_datetime
        ? formatDayAndHour(shipping.shipping_status_datetime)
        : '',
      incident: {
        description: parseString(shipping),
        code: shipping.incident_type_code,
        severity: statusSeverityColor,
      },
      manifestedDate: shipping.manifest_datetime
        ? format(new Date(shipping.manifest_datetime), 'dd/MM/yyyy')
        : '',
      deliveryDate: shipping.pickup?.date
        ? format(new Date(shipping.pickup.date), 'dd/MM/yyyy')
        : null,
      hasSolvableIncident: shipping?.has_incident_management ?? false,
      relatedReturn: {
        originalShippingCode: getOriginalShippingCode(shipping),
        returnShippingCode: getReturnShippingCode(shipping),
        reverseReturnShippingCode: getReverseReturnShippingCode(shipping),
        returnOnDeliveryShippingCode: getReturnOnDeliveryShippingCode(shipping),
      },
      pickupUnscheduled: isUnscheduled(shipping),
      delivery: shipping?.delivery,
    },
    details: {
      severity: '',
    },
  }
}

const formatShippingsData = (shippingsData, statusList) =>
  shippingsData?.map(datum => parseShipping(datum, statusList))

const parseToShippingDetail = shipping => {
  const { info } = shipping

  const shippingWithRefund = findAdditional(
    info.services,
    SHIPPING_WITH_REFUND_CODE,
  )
  const shippingWithInsuranceMerchandise = findAdditional(
    info.services,
    SHIPPING_WITH_INSURANCE_MERCHAND_CODE,
  )

  const shippingWithReturn = findAdditional(info.services, SHIPPING_WITH_RETURN)

  const shippingWithScanClientSlip = findAdditional(
    info.services,
    SHIPPING_WITH_SLIP_SCAN,
  )

  const shippingWithERE = findAdditional(info.services, SHIPPING_WITH_ERE)

  const shippingServices = []

  if (!!shippingWithRefund) {
    shippingServices.push({
      title: 'noun:refund-amount',
      value: shippingWithRefund.additional_value,
      extraInfo: 'field:money-suffix',
    })
  }

  if (!!shippingWithInsuranceMerchandise) {
    shippingServices.push({
      title: 'noun:insurance-merchandise',
      value: shippingWithInsuranceMerchandise.additional_value,
      extraInfo: 'field:money-suffix',
    })
  }

  if (!!shippingWithReturn) {
    shippingServices.push({
      title: 'service:with-return',
    })
  }

  if (!!shippingWithScanClientSlip) {
    shippingServices.push({
      title: 'service:scan-client-slip',
    })
  }

  if (!!shippingWithERE) {
    shippingServices.push({
      title: 'service:rdp-send',
      code: 'ERE',
    })
  }
  return {
    from: {
      client: {
        name: 'Centro Cero',
        code: info.clientCenterCode,
      },
      addressState: 'CLIENT',
      name: info.senderName,
      address: info.senderAddress2,
      country: info.originCountryCode,
      postalCode: {
        postalCode: info.originPostalCode,
        city: info.senderAddress,
      },
      contactPerson: info.senderName,
      phone: info.senderPhone,
      email: info.senderEmail,
      pickUpDate: info.declaredDeliveryDatetime,
      pickUpTimeSlot: 'esto no sale',
      comments: info.shippingComments,
    },
    to: {
      isNewDirection: true,
      name: info.destinName,
      address: info.destinAddress2,
      country: info.destinCountryCode,
      postalCode: {
        postalCode: info.destinPostalCode,
        city: info.destinAddress,
      },
      contactPerson: info.recipientName,

      phone: info.recipientPhone,
      email: info.recipientEmail,
      department: '',
      comments: info.conversionComments,
      pudoName: info?.delivery?.contact_name,
    },
    shipment: {
      serviceType: info.shippingTypeCode,
      referenceNumber: info.clientReference,
      packages: info.shippingItems,
    },
    services: shippingServices,
    info,
    //TODO REFACTOR FOR NOT NEEDING EXTRA PARSE IN DETAIL
  }
}

const parseShippingDetailResponse = (data, states, statusList) =>
  parseToShippingDetail(parseShipping(data, statusList, states))

const parseShippingsTrackingResponse = (
  shippingsResponse,
  statusList,
  type,
) => {
  const { data, pagination } = shippingsResponse
  const shippings = {
    shippings: formatShippingsData(data, statusList),
    pagination: {
      rowsPerPage: pagination.page_limit,
      //NOTE: The table is 0-indexed but backend is 1-indexed
      page: pagination.page_offsets.current - 1,
      totalRows: pagination.record_count,
    },
  }

  // filter by unscheduled pickups shippings
  if (
    type === TRACKING_OPTIONS.unscheduledPickups &&
    Array.isArray(shippings.shippings)
  ) {
    shippings.shippings = shippings.shippings.filter(
      shipping => shipping.info.pickupUnscheduled,
    )
  }

  return shippings
}

const parseFullTrackingResponse = fullTrackingResponse => {
  return fullTrackingResponse.data.shipping_history.events
    .sort((a, b) =>
      isBefore(new Date(a.event_date), new Date(b.event_date)) ? -1 : 1,
    )
    .map(step => ({
      committedDeliveryDatetime:
        step.committed_delivery_datetime === ''
          ? undefined
          : step.committed_delivery_datetime,
      itemCode: step.item_code,
      dateTime: format(new Date(step.event_date), 'dd/MM/yyyy | HH:mm'),
      eventName: capitalize(step.description),
      shippingStatusCode: step.code,
      incidentCode: step.detail.incident_type_code,
      incidentDescription: step.detail.incident_type_name,
      type: step.type,
    }))
}

const parseShippingStatesResponse = responseData => {
  return responseData.data.map(item => {
    const statusEvents = item.shipping_history.events.filter(
      event => event.type === 'STATUS',
    )
    const lastStatusEvent =
      statusEvents.length > 0 ? statusEvents[statusEvents.length - 1] : null
    //ÑAPA CODIGO WEB1600
    return lastStatusEvent
      ? {
          additionals: item?.additionals,
          destinCountryCode: item.destin_country_code,
          destinPostalCode: item.destin_postal_code,
          destinAddress: item.destin_address,
          destinAddress2: item.destin_town_name,
          shippingCode: item.shipping_code,
          lastStatusCode: lastStatusEvent.code,
          lastStatusDescription: lastStatusEvent.description,
          incidentDescription: lastStatusEvent?.detail?.incident_type_name
            ? lastStatusEvent.detail.incident_type_name
            : null,
          incidentCode: lastStatusEvent.detail.incident_type_code
            ? lastStatusEvent.detail.incident_type_code
            : lastStatusEvent.code === 'WEB1600',
          severity: getColorChipForStatusState({
            statusCode: lastStatusEvent.code,
            incidentCode: lastStatusEvent.detail.incident_type_code
              ? lastStatusEvent.detail.incident_type_code
              : lastStatusEvent.code === 'WEB1600',
            isCancelled: null,
            isUnscheduledPickup: isUnscheduled(item),
            isResolved:
              RESOLVED_STATUS_CODES.includes(
                item.shipping_history?.events[
                  item.shipping_history?.events?.length - 1
                ]?.code,
              ) &&
              INCIDENTS_CODES_ALLOWED_FOR_RESOLUTION.includes(
                lastStatusEvent.detail.incident_type_code,
              ),
          }),
        }
      : {
          additionals: item?.additionals,
          destinCountryCode: item.destin_country_code,
          destinPostalCode: item.destin_postal_code,
          destinAddress: item.destin_address,
          destinAddress2: item.destin_town_name,
          shippingCode: item.shipping_code,
          lastStatusCode: 'WEB0000',
          lastStatusDescription: 'Grabado',
          incidentCode: false,
          severity: 'turquoise',
        }
  })
}

const parseShippingSearchResponse = (shippingResponse, statusList) => {
  if (!Boolean(shippingResponse)) {
    return []
  }
  let shippingsResp = shippingResponse
  if (!Array.isArray(shippingsResp)) shippingsResp = [shippingsResp]
  return formatShippingsData(shippingsResp, statusList)
}

export {
  findAdditional,
  getShippingState,
  transformFiltersToBody,
  transformFiltersToBodyFile,
  parseShippingsTrackingResponse,
  parseFullTrackingResponse,
  parseShippingSearchResponse,
  parseShipping,
  parseShippingDetailResponse,
  parseShippingStatesResponse,
}
